import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import { useRepository } from '../../data/RepositoryProvider';
import { MutationError, toDialogError } from '../partners/PartnerFormDialog';
import { memberRepositoryToken } from './repository';
import type { MemberDetail, MemberStatus } from './types';

export function MemberStatusDialog({
  member,
  onClose,
  onSaved,
  open,
}: {
  readonly member: MemberDetail;
  readonly onClose: () => void;
  readonly onSaved: (member: MemberDetail) => void;
  readonly open: boolean;
}) {
  const repository = useRepository(memberRepositoryToken);
  const { t } = useTranslation();
  const guard = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm<{ status: MemberStatus; reason: string }>({
    defaultValues: { status: member.status, reason: '' },
  });
  const proposed = watch('status');
  useEffect(() => {
    if (!open) return;
    reset({ status: member.status, reason: '' });
    setError(null);
    guard.current = false;
    setBusy(false);
  }, [member.status, open, reset]);
  const close = () => {
    if (!guard.current) onClose();
  };
  const submit = handleSubmit(async (values) => {
    if (guard.current) return;
    guard.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await repository.changeStatus(member.id, {
        status: values.status,
        reason: values.reason.trim(),
      });
      onSaved(result.data);
      onClose();
    } catch (submitError) {
      setError(toDialogError(submitError, t('members.errors.mutation')));
    } finally {
      guard.current = false;
      setBusy(false);
    }
  });
  return (
    <Dialog aria-describedby="member-status-impact" fullWidth onClose={close} open={open}>
      <DialogTitle>{t('members.status.title')}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="member-status-form"
          noValidate
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <Alert severity="warning">
            <Typography id="member-status-impact">
              {t('members.status.impact', {
                username: member.username ?? member.id,
                current: member.status,
                next: proposed,
              })}
            </Typography>
            <Typography variant="body2">{t('members.status.closedTerminal')}</Typography>
          </Alert>
          <TextField
            defaultValue={member.status}
            label={t('members.fields.proposedStatus')}
            select
            {...register('status')}
          >
            <MenuItem value="ACTIVE">ACTIVE</MenuItem>
            <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
            <MenuItem value="CLOSED">CLOSED</MenuItem>
          </TextField>
          <TextField
            error={errors.reason !== undefined}
            helperText={errors.reason?.message}
            label={t('members.fields.reason')}
            multiline
            required
            {...register('reason', {
              required: t('members.validation.reasonRequired'),
              validate: (value) =>
                value.trim().length > 0 || t('members.validation.reasonRequired'),
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={close}>
          {t('actions.cancel')}
        </Button>
        <Button
          color="warning"
          form="member-status-form"
          loading={busy}
          type="submit"
          variant="contained"
        >
          {t('actions.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
